import React from "react";
import { useTranslation } from "react-i18next";
import {
  UtensilsCrossed,
  ShoppingBag,
  Shirt,
  Sofa,
  HeartPulse,
  Briefcase,
  BedDouble,
  Palette,
  ArrowRight,
} from "lucide-react";
import { LLink } from "@/i18n/LLink";
import { useLang } from "@/i18n/LangContext";
import { pageContent, pickLang } from "@/i18n/pageContent";

// 8 zakelijke hoofdcategorieën — 2×4 grid op desktop.
const CATEGORIES = [
  { key: "eten-drinken",      to: "/eten-en-drinken",     icon: UtensilsCrossed, image: "/media/lemmer-terras.jpg" },
  { key: "winkelen",          to: "/winkelen",            icon: ShoppingBag,     image: "/media/lemmer-winkelen.jpg" },
  { key: "mode",              to: "/mode",                icon: Shirt,           image: "/media/lemmer-kortestreek.jpg" },
  { key: "wonen-interieur",   to: "/wonen-interieur",     icon: Sofa,            image: "/media/oc-interiors.jpg" },
  { key: "gezondheid-beauty", to: "/gezondheid-beauty",   icon: HeartPulse,      image: "/media/lemmer-strand.jpg" },
  { key: "diensten",          to: "/diensten",            icon: Briefcase,       image: "/media/lemmer-centrum.jpg" },
  { key: "overnachten",       to: "/overnachten",         icon: BedDouble,       image: "/media/lemmer-haven-2.jpg" },
  { key: "cultuur-vrije-tijd",to: "/cultuur-vrije-tijd",  icon: Palette,         image: "/media/lemmer-festival.jpg" },
];

export const CategoryTilesSection = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const c = pickLang(pageContent.home, lang).categories;
  return (
    <section
      data-testid="home-categories"
      className="ll-section-soft py-16 sm:py-20"
    >
      <div className="ll-container">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <span className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3">
            {c.eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            {c.title}
          </h2>
          <p className="mt-3 text-[#3E5871] font-[Inter] text-base sm:text-lg">
            {c.intro}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const item = c.items[cat.key] || {};
            return (
              <LLink
                key={cat.key}
                to={cat.to}
                data-testid={`category-tile-${cat.key}`}
                className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 aspect-[4/3] bg-[#173A5E]"
              >
                <img
                  src={cat.image}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(23,58,94,0.15) 0%, rgba(23,58,94,0.85) 100%)",
                  }}
                />
                <div className="relative z-10 h-full flex flex-col justify-end p-5 sm:p-6">
                  <div className="w-11 h-11 rounded-full bg-white/95 flex items-center justify-center mb-3 shadow-md">
                    <Icon className="w-5 h-5 text-[#2E9CD6]" />
                  </div>
                  <h3 className="text-white text-xl sm:text-2xl font-[Poppins] font-bold mb-1 leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-white/85 font-[Inter] text-sm mb-3 line-clamp-2">
                    {item.intro}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#6BC0E8] font-[Poppins] font-semibold text-sm group-hover:gap-2 transition-all">
                    {t("common.ontdek")}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </LLink>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CategoryTilesSection;
